import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { Settings, Printer, TestTube, Wifi, WifiOff, Save } from 'lucide-react';
import axios from 'axios';
import thermalPrinter, { ThermalPrinterService } from '../services/thermalPrinterService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Toggle Switch Component
const ToggleSwitch = ({ checked, onChange, label }) => (
  <div className="flex items-center justify-between py-2">
    <span className="text-sm text-primary">{label}</span>
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        checked ? 'bg-accent' : 'bg-gray-300'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  </div>
);

const PrinterSettings = ({ isOpen, onClose, franchiseId }) => {
  const [settings, setSettings] = useState({
    franchise_id: franchiseId,
    paper_width: '80mm',
    font_size: 'medium',
    copies: 1,
    header_text: 'Welcome to 1946',
    footer_text: 'Thank you for your visit!',
    show_logo: true,
    auto_print: false,
    show_franchise_name: true,
    show_franchise_address: true,
    show_franchise_contact: true,
    show_date_time: true,
    show_order_number: true,
    show_item_details: true,
    show_payment_method: true,
    show_total: true
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [printerConnected, setPrinterConnected] = useState(false);
  const [printerName, setPrinterName] = useState('');
  const [isUSBSupported, setIsUSBSupported] = useState(false);
  const [printMode, setPrintMode] = useState('browser'); // 'browser' or 'usb'

  useEffect(() => {
    const usbSupported = ThermalPrinterService.isUSBSupported();
    console.log('USB Supported:', usbSupported);
    setIsUSBSupported(usbSupported);
    
    if (isOpen && franchiseId) {
      fetchSettings();
      // Try to reconnect to previously paired printer if USB is supported
      if (usbSupported) {
        tryReconnectPrinter();
      }
    }
  }, [isOpen, franchiseId]);

  const tryReconnectPrinter = async () => {
    try {
      const result = await thermalPrinter.reconnectPairedDevice();
      if (result && result.success) {
        setPrinterConnected(true);
        setPrinterName(result.deviceName);
        setPrintMode('usb');
        thermalPrinter.setPrintMode('usb');
        console.log('Auto-reconnected to printer:', result.deviceName);
      }
    } catch (error) {
      console.log('No paired printer to reconnect');
    }
  };

  const handlePrintModeChange = (mode) => {
    setPrintMode(mode);
    thermalPrinter.setPrintMode(mode);
    if (mode === 'browser') {
      // Disconnect USB if switching to browser mode
      if (printerConnected) {
        handleDisconnectPrinter();
      }
    }
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/printer/settings/${franchiseId}`);
      if (response.data.settings) {
        setSettings({ ...settings, ...response.data.settings });
      }
    } catch (error) {
      console.error('Error fetching printer settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/printer/settings/${franchiseId}`, settings);
      toast.success('Printer settings saved!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleConnectPrinter = async () => {
    if (!isUSBSupported) {
      const diag = ThermalPrinterService.getDiagnostics();
      console.log('USB Diagnostics:', diag);
      toast.error(`USB not available. Protocol: ${diag.protocol}, Secure: ${diag.isSecureContext}`);
      return;
    }
    
    try {
      const result = await thermalPrinter.requestDevice();
      setPrinterConnected(true);
      setPrinterName(result.deviceName);
      setPrintMode('usb');
      thermalPrinter.setPrintMode('usb');
      toast.success(`Connected to ${result.deviceName}`);
    } catch (error) {
      console.error('Error connecting printer:', error);
      setPrinterConnected(false);
      setPrinterName('');
      
      // Show specific error message
      const errorMessage = error.message || 'Failed to connect to printer';
      toast.error(errorMessage);
    }
  };

  const handleDisconnectPrinter = async () => {
    try {
      await thermalPrinter.disconnect();
      setPrinterConnected(false);
      setPrinterName('');
      toast.success('Printer disconnected');
    } catch (error) {
      console.error('Error disconnecting:', error);
    }
  };

  const handleTestPrint = async () => {
    if (!printerConnected) {
      toast.error('Please connect a printer first');
      return;
    }

    try {
      await thermalPrinter.testPrint(settings);
      toast.success('Test print sent!');
    } catch (error) {
      console.error('Error test printing:', error);
      toast.error('Failed to print test receipt');
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-surface max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <Settings className="h-6 w-6" />
            Printer Settings
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center">
            <div className="animate-spin h-8 w-8 border-4 border-accent border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-2 text-secondary">Loading settings...</p>
          </div>
        ) : (
          <div className="space-y-6 mt-4">
            {/* Printer Connection */}
            <Card className="border-2 border-accent/20">
              <CardContent className="p-4">
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <Printer className="h-5 w-5" />
                  Print Method
                </h3>
                
                {/* Print Mode Selection */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <button
                    onClick={() => handlePrintModeChange('browser')}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      printMode === 'browser' 
                        ? 'border-accent bg-accent/10' 
                        : 'border-border hover:border-accent/50'
                    }`}
                  >
                    <div className="font-medium text-primary">Browser Print</div>
                    <div className="text-xs text-secondary">Works everywhere</div>
                  </button>
                  <button
                    onClick={() => handlePrintModeChange('usb')}
                    disabled={!isUSBSupported}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      printMode === 'usb' 
                        ? 'border-accent bg-accent/10' 
                        : 'border-border hover:border-accent/50'
                    } ${!isUSBSupported ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="font-medium text-primary">USB Direct</div>
                    <div className="text-xs text-secondary">
                      {isUSBSupported ? 'ESC/POS commands' : 'Requires HTTPS'}
                    </div>
                  </button>
                </div>

                {printMode === 'browser' ? (
                  <div className="space-y-3">
                    <div className="p-3 bg-green-50 rounded-lg text-green-700 text-sm">
                      <p className="font-medium">✓ Browser Print Mode Active</p>
                      <p>Click &quot;Save &amp; Print&quot; to open the system print dialog. Select your thermal printer from the list.</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleTestPrint}
                      className="w-full"
                    >
                      <TestTube className="h-4 w-4 mr-2" />
                      Test Print
                    </Button>
                  </div>
                ) : !isUSBSupported ? (
                  <div className="p-4 bg-yellow-50 rounded-lg text-yellow-700 text-sm">
                    <p className="font-medium">⚠️ USB Not Available</p>
                    <p>USB printing requires HTTPS or localhost. Please use Browser Print mode instead.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <div className="flex items-center gap-2">
                        {printerConnected ? (
                          <Wifi className="h-5 w-5 text-green-500" />
                        ) : (
                          <WifiOff className="h-5 w-5 text-gray-400" />
                        )}
                        <div>
                          <p className="font-medium text-primary">
                            {printerConnected ? printerName : 'No printer connected'}
                          </p>
                          <p className="text-xs text-secondary">
                            {printerConnected ? 'Ready to print via USB' : 'Click connect to pair a printer'}
                          </p>
                        </div>
                      </div>
                      
                      {printerConnected ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleDisconnectPrinter}
                          className="text-red-500 border-red-500 hover:bg-red-50"
                        >
                          Disconnect
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={handleConnectPrinter}
                          className="bg-accent hover:bg-accent/90"
                        >
                          Connect Printer
                        </Button>
                      )}
                    </div>

                    {printerConnected && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleTestPrint}
                        className="w-full"
                      >
                        <TestTube className="h-4 w-4 mr-2" />
                        Test Print
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Paper & Print Settings */}
            <Card>
              <CardContent className="p-4">
                <h3 className="font-bold text-lg mb-3">Paper & Print Settings</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  {/* Paper Width */}
                  <div>
                    <label className="text-sm font-medium text-secondary mb-1 block">Paper Width</label>
                    <select
                      value={settings.paper_width}
                      onChange={(e) => updateSetting('paper_width', e.target.value)}
                      className="w-full h-10 px-3 rounded-lg border border-border bg-background text-primary"
                    >
                      <option value="58mm">58mm (2 inch)</option>
                      <option value="80mm">80mm (3 inch)</option>
                    </select>
                  </div>

                  {/* Font Size */}
                  <div>
                    <label className="text-sm font-medium text-secondary mb-1 block">Font Size</label>
                    <select
                      value={settings.font_size}
                      onChange={(e) => updateSetting('font_size', e.target.value)}
                      className="w-full h-10 px-3 rounded-lg border border-border bg-background text-primary"
                    >
                      <option value="small">Small</option>
                      <option value="medium">Medium</option>
                      <option value="large">Large</option>
                    </select>
                  </div>

                  {/* Number of Copies */}
                  <div>
                    <label className="text-sm font-medium text-secondary mb-1 block">Copies</label>
                    <Input
                      type="number"
                      min="1"
                      max="5"
                      value={settings.copies}
                      onChange={(e) => updateSetting('copies', parseInt(e.target.value) || 1)}
                      className="h-10"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <ToggleSwitch
                    checked={settings.auto_print}
                    onChange={(val) => updateSetting('auto_print', val)}
                    label="Auto-print on Save & Print"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Header & Footer */}
            <Card>
              <CardContent className="p-4">
                <h3 className="font-bold text-lg mb-3">Header & Footer</h3>
                
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-secondary mb-1 block">Header Text</label>
                    <Input
                      value={settings.header_text}
                      onChange={(e) => updateSetting('header_text', e.target.value)}
                      placeholder="Welcome message..."
                      className="h-10"
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-secondary mb-1 block">Footer Text</label>
                    <Input
                      value={settings.footer_text}
                      onChange={(e) => updateSetting('footer_text', e.target.value)}
                      placeholder="Thank you message..."
                      className="h-10"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Receipt Content */}
            <Card>
              <CardContent className="p-4">
                <h3 className="font-bold text-lg mb-3">Receipt Content</h3>
                
                <div className="space-y-1">
                  <ToggleSwitch
                    checked={settings.show_logo}
                    onChange={(val) => updateSetting('show_logo', val)}
                    label="Show Logo"
                  />
                  <ToggleSwitch
                    checked={settings.show_franchise_name}
                    onChange={(val) => updateSetting('show_franchise_name', val)}
                    label="Show Franchise Name"
                  />
                  <ToggleSwitch
                    checked={settings.show_franchise_address}
                    onChange={(val) => updateSetting('show_franchise_address', val)}
                    label="Show Franchise Address"
                  />
                  <ToggleSwitch
                    checked={settings.show_franchise_contact}
                    onChange={(val) => updateSetting('show_franchise_contact', val)}
                    label="Show Franchise Contact"
                  />
                  <ToggleSwitch
                    checked={settings.show_date_time}
                    onChange={(val) => updateSetting('show_date_time', val)}
                    label="Show Date & Time"
                  />
                  <ToggleSwitch
                    checked={settings.show_order_number}
                    onChange={(val) => updateSetting('show_order_number', val)}
                    label="Show Order Number"
                  />
                  <ToggleSwitch
                    checked={settings.show_item_details}
                    onChange={(val) => updateSetting('show_item_details', val)}
                    label="Show Item Details"
                  />
                  <ToggleSwitch
                    checked={settings.show_payment_method}
                    onChange={(val) => updateSetting('show_payment_method', val)}
                    label="Show Payment Method"
                  />
                  <ToggleSwitch
                    checked={settings.show_total}
                    onChange={(val) => updateSetting('show_total', val)}
                    label="Show Total Amount"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Save Button */}
            <Button
              onClick={handleSaveSettings}
              disabled={saving}
              className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-bold"
            >
              {saving ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PrinterSettings;
